/**
 * Admin server functions for the video library, video placements and courses —
 * admin auth, input validation, and delegation to `~/lib/server/learning`.
 *
 * Aliases: videos admin api, courses admin api, placements admin api
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";

import { permissionMiddleware } from "~/lib/permission-middleware";
import {
  courseInputSchema,
  createCourseWeekInputSchema,
  idInputSchema,
  lookupVideoInputSchema,
  reorderCourseWeeksInputSchema,
  setCourseStatusInputSchema,
  setPlacementVideoInputSchema,
  updateCourseDetailsInputSchema,
  updateCourseWeekInputSchema,
  updateVideoInputSchema,
  videoInputSchema,
} from "~/lib/shared/learning/admin-inputs";

// Type-only re-exports, erased at build, so a route component never imports
// from `~/lib/server`.
export type {
  AdminCourse,
  AdminCourseMaterial,
  AdminCourseSummary,
  AdminCourseWeek,
  DeleteCourseResult,
} from "~/lib/server/learning/courses";
export type { PlacementAssignments } from "~/lib/server/learning/placements";
export type { LibraryVideo, VideoLookup } from "~/lib/server/learning/videos";

export const learningAdminKeys = {
  all: ["admin-learning"] as const,
  videos: ["admin-learning", "videos"] as const,
  placements: ["admin-learning", "placements"] as const,
  courses: ["admin-learning", "courses"] as const,
  course: (id: string) => ["admin-learning", "courses", id] as const,
};

// ── Videos ──────────────────────────────────────────────────────────────────

export const listVideosFn = createServerFn({ method: "GET" })
  .middleware([permissionMiddleware("videos:read")])
  .handler(async () => {
    const { listLibraryVideos } = await import("~/lib/server/learning/videos");
    return listLibraryVideos();
  });

/** Fills a Wistia video's title, length and thumbnail from its link. */
export const lookupVideoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("videos:read")])
  .validator(lookupVideoInputSchema)
  .handler(async ({ data }) => {
    const { lookupVideoDetails } = await import("~/lib/server/learning/videos");
    return lookupVideoDetails(data.url);
  });

export const createVideoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("videos:write")])
  .validator(videoInputSchema)
  .handler(async ({ data }) => {
    const { createVideo } = await import("~/lib/server/learning/videos");
    const video = await createVideo(data);
    return { id: video.id };
  });

export const updateVideoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("videos:write")])
  .validator(updateVideoInputSchema)
  .handler(async ({ data }) => {
    const { updateVideo } = await import("~/lib/server/learning/videos");
    await updateVideo(data);
  });

/** Refused, with the places to change first, while anything shows the video. */
export const deleteVideoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("videos:write")])
  .validator(idInputSchema)
  .handler(async ({ data }) => {
    const { deleteVideo } = await import("~/lib/server/learning/videos");
    return deleteVideo(data.id);
  });

export const videosQueryOptions = () =>
  queryOptions({
    queryKey: learningAdminKeys.videos,
    queryFn: () => listVideosFn(),
  });

// ── Placements ──────────────────────────────────────────────────────────────

export const listPlacementsFn = createServerFn({ method: "GET" })
  .middleware([permissionMiddleware("placements:read")])
  .handler(async () => {
    const { listPlacementAssignments } =
      await import("~/lib/server/learning/placements");
    return listPlacementAssignments();
  });

export const setPlacementVideoFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("placements:write")])
  .validator(setPlacementVideoInputSchema)
  .handler(async ({ data }) => {
    const { setPlacementVideo } =
      await import("~/lib/server/learning/placements");
    await setPlacementVideo(data);
  });

export const placementsQueryOptions = () =>
  queryOptions({
    queryKey: learningAdminKeys.placements,
    queryFn: () => listPlacementsFn(),
  });

// ── Courses ─────────────────────────────────────────────────────────────────

export const listCoursesFn = createServerFn({ method: "GET" })
  .middleware([permissionMiddleware("courses:read")])
  .handler(async () => {
    const { listCourses } = await import("~/lib/server/learning/courses");
    return listCourses();
  });

export const getCourseFn = createServerFn({ method: "GET" })
  .middleware([permissionMiddleware("courses:read")])
  .validator(idInputSchema)
  .handler(async ({ data }) => {
    const { getCourseForAdmin } = await import("~/lib/server/learning/courses");
    return getCourseForAdmin(data.id);
  });

export const createCourseFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("courses:write")])
  .validator(courseInputSchema)
  .handler(async ({ data }) => {
    const { createCourse } = await import("~/lib/server/learning/courses");
    return createCourse(data);
  });

export const updateCourseDetailsFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("courses:write")])
  .validator(updateCourseDetailsInputSchema)
  .handler(async ({ data }) => {
    const { updateCourseDetails } =
      await import("~/lib/server/learning/courses");
    return updateCourseDetails(data);
  });

export const setCourseStatusFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("courses:write")])
  .validator(setCourseStatusInputSchema)
  .handler(async ({ data }) => {
    const { setCourseStatus } = await import("~/lib/server/learning/courses");
    await setCourseStatus(data);
  });

export const deleteCourseFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("courses:write")])
  .validator(idInputSchema)
  .handler(async ({ data }) => {
    const { deleteCourse } = await import("~/lib/server/learning/courses");
    return deleteCourse(data.id);
  });

export const createCourseWeekFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("courses:write")])
  .validator(createCourseWeekInputSchema)
  .handler(async ({ data }) => {
    const { createCourseWeek } = await import("~/lib/server/learning/courses");
    return createCourseWeek(data);
  });

export const updateCourseWeekFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("courses:write")])
  .validator(updateCourseWeekInputSchema)
  .handler(async ({ data }) => {
    const { updateCourseWeek } = await import("~/lib/server/learning/courses");
    await updateCourseWeek(data);
  });

export const deleteCourseWeekFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("courses:write")])
  .validator(idInputSchema)
  .handler(async ({ data }) => {
    const { deleteCourseWeek } = await import("~/lib/server/learning/courses");
    await deleteCourseWeek(data.id);
  });

export const reorderCourseWeeksFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("courses:write")])
  .validator(reorderCourseWeeksInputSchema)
  .handler(async ({ data }) => {
    const { reorderCourseWeeks } =
      await import("~/lib/server/learning/courses");
    await reorderCourseWeeks(data);
  });

export const coursesQueryOptions = () =>
  queryOptions({
    queryKey: learningAdminKeys.courses,
    queryFn: () => listCoursesFn(),
  });

export const courseQueryOptions = (id: string) =>
  queryOptions({
    queryKey: learningAdminKeys.course(id),
    queryFn: () => getCourseFn({ data: { id } }),
  });
